import {
  SequenceEmailIcon,
  SequencePencilIcon,
  SequenceReadyIcon,
  SequenceSmsIcon,
  SequenceTrashIcon,
  SequenceWarningIcon
} from 'features/CustomCampaigns/EditCampaign/CampaignFlowIcons'
import {
  dripMediumOptionsWithEmail,
  dripMediumOptionsWithText
} from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import { getDripMediumIcon } from 'features/CustomCampaigns/EditCampaign/dripMediumMeta'

import {
  getEmailChannelStatus,
  getTextChannelStatus,
  SEQUENCE_CHANNEL_STATUS
} from '../dripSequenceHelpers'
import styles from './DripStepCard.module.scss'

const TEXT_CHANNEL_DESCRIPTION =
  'Edit & personalize the message you want to send to guests.'

function StatusBadge({ status }) {
  if (status === SEQUENCE_CHANNEL_STATUS.ready) {
    return (
      <span className={styles.statusReady}>
        <SequenceReadyIcon />
        Ready
      </span>
    )
  }

  if (status === SEQUENCE_CHANNEL_STATUS.needsRefinement) {
    return (
      <span className={styles.statusPending}>
        <SequenceWarningIcon />
        Content needs refinement
      </span>
    )
  }

  return null
}

function ChannelPanel({
  channelIcon: ChannelIcon,
  status,
  label,
  name,
  description,
  emptyTitle,
  emptyDescription,
  emptyActionLabel,
  onEmptyAction,
  onChangeTemplate,
  onEdit,
  onClear
}) {
  const isEmpty = status === SEQUENCE_CHANNEL_STATUS.empty

  return (
    <div className={isEmpty ? styles.channelPanelEmpty : styles.channelPanel}>
      <span className={styles.channelBadge} aria-hidden="true">
        <ChannelIcon />
        {!isEmpty && <i className={styles.channelBadgeDot} />}
      </span>
      {isEmpty ? (
        <div className={styles.emptyContent}>
          <strong className={styles.emptyTitle}>{emptyTitle}</strong>
          <span className={styles.emptyDescription}>{emptyDescription}</span>
          <button type="button" className={styles.primaryAction} onClick={onEmptyAction}>
            {emptyActionLabel}
          </button>
        </div>
      ) : (
        <>
          <span className={styles.channelLabel}>{label}</span>
          <div className={styles.channelNameRow}>
            <strong className={styles.channelName}>{name}</strong>
            <StatusBadge status={status} />
          </div>
          <p className={styles.channelDescription}>{description}</p>
          <div className={styles.channelActions}>
            {onChangeTemplate && (
              <button
                type="button"
                className={styles.secondaryAction}
                onClick={onChangeTemplate}
              >
                Change template
              </button>
            )}
            <button type="button" className={styles.primaryAction} onClick={onEdit}>
              <SequencePencilIcon className={styles.actionIcon} />
              Edit message
            </button>
            <button
              type="button"
              className={styles.iconAction}
              onClick={onClear}
              aria-label="Clear this channel content"
              title="Clear this channel content"
            >
              <SequenceTrashIcon />
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function DripStepCard({
  step,
  title,
  subtitle,
  medium,
  errorMessage,
  canRemove = false,
  onEditText,
  onEditEmail,
  onSelectEmailTemplate,
  onChangeEmailTemplate,
  onClearText,
  onClearEmail,
  onRemove
}) {
  const hasText = dripMediumOptionsWithText.includes(medium)
  const hasEmail = dripMediumOptionsWithEmail.includes(medium)
  const isDualChannel = hasText && hasEmail

  const StepIcon = getDripMediumIcon(medium)

  const cardClassNames = [
    isDualChannel ? styles.cardDual : styles.cardSingle,
    errorMessage && styles.hasError
  ]
    .filter(Boolean)
    .join(' ')

  const header = (
    <div className={styles.headerPane}>
      <span className={styles.stepIcon} aria-hidden="true">
        <StepIcon />
      </span>
      <strong className={styles.stepTitle}>{title}</strong>
      <span className={styles.stepSubtitle}>{subtitle}</span>
      {canRemove && (
        <button
          type="button"
          className={styles.removeStepButton}
          onClick={onRemove}
          title="Remove this follow-up from the sequence"
        >
          <SequenceTrashIcon className={styles.removeStepIcon} />
          Remove follow-up
        </button>
      )}
    </div>
  )

  const textPanel = hasText && (
    <ChannelPanel
      channelIcon={SequenceSmsIcon}
      status={getTextChannelStatus(step)}
      label="Personalize SMS"
      name="Text Message"
      description={TEXT_CHANNEL_DESCRIPTION}
      emptyTitle="Text Message"
      emptyDescription="No message written yet"
      emptyActionLabel="Write message"
      onEmptyAction={onEditText}
      onEdit={onEditText}
      onClear={onClearText}
    />
  )

  const emailPanel = hasEmail && (
    <ChannelPanel
      channelIcon={SequenceEmailIcon}
      status={getEmailChannelStatus(step)}
      label="Template"
      name={step?.emailTemplateName || 'Default'}
      description={
        step?.emailTemplateDescription ||
        'Clean and modern layout with a focused call to action.'
      }
      emptyTitle="Email"
      emptyDescription="No email template selected yet"
      emptyActionLabel="Select template"
      onEmptyAction={onSelectEmailTemplate}
      onChangeTemplate={onChangeEmailTemplate}
      onEdit={onEditEmail}
      onClear={onClearEmail}
    />
  )

  return (
    <div className={cardClassNames}>
      {header}
      <div className={styles.channelRow}>
        {textPanel}
        {emailPanel}
      </div>
      {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
    </div>
  )
}

export default DripStepCard
